import { IconProps } from '@euler/app/components/icons/types';
import { useSvgProps } from '@euler/app/components/icons/util';
import { Center } from '@euler/components/Center';
import Euler from '@euler/components/layout';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { useBehaviorSubject, usePersistFn } from '@euler/utils/hooks';
import { FC, memo, useCallback, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  NativeSyntheticEvent,
  Platform,
  StyleProp,
  TextInput,
  TextInputKeyPressEventData,
  TouchableOpacity,
  ViewStyle,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { BehaviorSubject } from 'rxjs';
import { useTheme } from 'styled-components';

const kSearchInputDefaultHeight = 44;

export const SearchInput = memo(
  ({
    query,
    placeholder,
    placeholderTextColor = '#8C8C8C',
    isDarkMode,
    onQueryChange,
    style,
    searchIconColor,
    isLoading,
    onSearch,
  }: {
    query: string;
    placeholder?: string;
    placeholderTextColor?: string;
    isDarkMode?: boolean;
    onQueryChange: (query: string) => void;
    style?:
      | StyleProp<ViewStyle>
      | ((params: { isFocused: boolean }) => StyleProp<ViewStyle>);
    focusedStyle?: StyleProp<ViewStyle>;
    searchIconColor?: string;
    isLoading?: boolean;
    onSearch?: () => void;
  }) => {
    const { link: linkColor } = useTheme();
    const { rpx } = useResponsiveContext();
    const [isFocused, setIsFocused] = useState(false);
    const textInputRef = useRef<TextInput>(null);

    const onSubmitEditing = usePersistFn(() => {
      onSearch?.();
    });

    const onKeyPress = usePersistFn(
      (e: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
        if (Platform.OS !== 'web') return;
        if (e.nativeEvent.key === 'Enter') {
          onSearch?.();
        }
      },
    );

    const onFocus = useCallback(() => {
      setIsFocused(true);
    }, []);

    const onBlur = useCallback(() => {
      setIsFocused(false);
    }, []);

    const onClear = useCallback(() => {
      onQueryChange('');
    }, [onQueryChange]);

    const containerStyle = useMemo(() => {
      if (typeof style === 'function') {
        return style({ isFocused });
      }
      return style;
    }, [isFocused, style]);

    return (
      <Euler.View
        flexDirection="row"
        height={rpx(kSearchInputDefaultHeight)}
        paddingHorizontal={rpx(15)}
        borderRadius={rpx(kSearchInputDefaultHeight) / 2}
        backgroundColor={isDarkMode ? '#161A00' : '#fff'}
        style={containerStyle}
      >
        <TextInput
          ref={textInputRef}
          placeholder={placeholder ?? 'Search'}
          placeholderTextColor={placeholderTextColor}
          css={`
            flex: 1;
            font-family: ${FontFamily.NotoSans.Medium};
            font-size: ${rpx(14)}px;
            padding: 0;
            color: ${isDarkMode ? '#fff' : '#000'};
          `}
          value={query}
          onChangeText={onQueryChange}
          onSubmitEditing={onSubmitEditing}
          returnKeyType={'search'}
          onKeyPress={Platform.OS === 'web' ? onKeyPress : undefined}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {isLoading ? (
          <ActivityIndicator
            size="small"
            css={`
              width: ${rpx(16)}px;
              height: ${rpx(16)}px;
              margin-left: ${rpx(8)}px;
              transform: scale(0.8);
              align-self: center;
            `}
          />
        ) : null}
        {onSearch != null && !query ? (
          <Center.Touchable
            onPress={onSearch}
            alignSelf="center"
            ml={rpx(5)}
            mr={rpx(10)}
          >
            <SearchIcon
              width={rpx(24)}
              height={rpx(24)}
              fill={searchIconColor ?? (isDarkMode ? linkColor : undefined)}
            />
          </Center.Touchable>
        ) : query ? (
          <ClearButton
            onPress={onClear}
            css={`
              margin-left: ${rpx(5)}px;
            `}
          />
        ) : (
          <SearchIcon
            width={rpx(24)}
            height={rpx(24)}
            fill={searchIconColor ?? (isDarkMode ? linkColor : undefined)}
            css={`
              align-self: center;
              margin-left: ${rpx(5)}px;
            `}
          />
        )}
      </Euler.View>
    );
  },
);

export const WiredSearchInput = memo(
  ({
    query$,
    style,
    placeholder,
    placeholderTextColor,
    instanceSearchEnabled,
    searchIconColor,
    clearQueryOnSearch,
    isLoading,
    ...props
  }: {
    /** when enabled, search will be executed on the fly w/o explicit action */
    instanceSearchEnabled?: boolean;
    query$: BehaviorSubject<string>;
    placeholder?: string;
    placeholderTextColor?: string;
    style?: StyleProp<ViewStyle>;
    clearQueryOnSearch?: boolean;
    searchIconColor?: string;
    isLoading?: boolean;
    onSearch?: (query: string) => void;
  }) => {
    const [query, updateQuery] = useBehaviorSubject(query$);
    const [localQuery, updateLocalQuery] = useState<string>(query);
    const onSearch = usePersistFn(() => {
      if (!instanceSearchEnabled) {
        updateQuery(clearQueryOnSearch ? '' : localQuery);
        if (clearQueryOnSearch) {
          updateLocalQuery('');
        }
        props.onSearch?.(localQuery);
      } else {
        props.onSearch?.(query);
      }
    });
    const onLocalQueryChange = usePersistFn((s: string) => {
      updateLocalQuery(s);
      if (!s) updateQuery(s);
    });
    return (
      <SearchInput
        query={instanceSearchEnabled ? query : localQuery}
        placeholder={placeholder}
        placeholderTextColor={placeholderTextColor}
        searchIconColor={searchIconColor}
        isLoading={isLoading}
        style={style}
        onQueryChange={instanceSearchEnabled ? updateQuery : onLocalQueryChange}
        onSearch={onSearch}
      />
    );
  },
);

const ClearButton = memo(
  ({
    onPress,
    style,
  }: {
    onPress?: () => void;
    style?: StyleProp<ViewStyle>;
  }) => {
    const { rpx } = useResponsiveContext();
    return (
      <TouchableOpacity
        css={`
          align-items: center;
          justify-content: center;
        `}
        style={style}
        onPress={onPress}
      >
        <CrossIcon width={rpx(22)} height={rpx(22)} />
      </TouchableOpacity>
    );
  },
);

const CrossIcon: FC<IconProps> = memo(props => {
  const { fill = '#8C8C8C', ...svgProps } = useSvgProps(props, 22, 22);
  return (
    <Svg viewBox="0 0 22 22" fill="none" {...svgProps}>
      <Path
        d="M11 0.9375C5.20156 0.9375 0.5 5.44316 0.5 11C0.5 16.5568 5.20156 21.0625 11 21.0625C16.7984 21.0625 21.5 16.5568 21.5 11C21.5 5.44316 16.7984 0.9375 11 0.9375ZM14.8766 14.8229L13.3297 14.8161L11 12.1545L8.67266 14.8139L7.12344 14.8206C7.02031 14.8206 6.93594 14.742 6.93594 14.6409C6.93594 14.5982 6.95234 14.5578 6.98047 14.5241L10.0297 11.0427L6.98047 7.56348C6.95215 7.53056 6.93643 7.48934 6.93594 7.44668C6.93594 7.34785 7.02031 7.26699 7.12344 7.26699L8.67266 7.27373L11 9.93535L13.3273 7.27598L14.8742 7.26924C14.9773 7.26924 15.0617 7.34785 15.0617 7.44893C15.0617 7.4916 15.0453 7.53203 15.0172 7.56572L11.9727 11.0449L15.0195 14.5264C15.0477 14.5601 15.0641 14.6005 15.0641 14.6432C15.0641 14.742 14.9797 14.8229 14.8766 14.8229Z"
        fill={fill}
      />
    </Svg>
  );
});

const SearchIcon: FC<IconProps> = memo(props => {
  const { fill = '#000', ...svgProps } = useSvgProps(props, 24, 24);
  return (
    <Svg viewBox="0 0 24 24" fill="none" {...svgProps}>
      <Path
        d="M21.3187 20.0273L15.232 13.9406C16.1766 12.7195 16.6875 11.2266 16.6875 9.65625C16.6875 7.77656 15.9539 6.01406 14.6273 4.68516C13.3008 3.35625 11.5336 2.625 9.65625 2.625C7.77891 2.625 6.01172 3.35859 4.68516 4.68516C3.35625 6.01172 2.625 7.77656 2.625 9.65625C2.625 11.5336 3.35859 13.3008 4.68516 14.6273C6.01172 15.9562 7.77656 16.6875 9.65625 16.6875C11.2266 16.6875 12.7172 16.1766 13.9383 15.2344L20.025 21.3187C20.0428 21.3366 20.064 21.3508 20.0874 21.3604C20.1107 21.3701 20.1357 21.3751 20.1609 21.3751C20.1862 21.3751 20.2112 21.3701 20.2345 21.3604C20.2578 21.3508 20.279 21.3366 20.2969 21.3187L21.3187 20.2992C21.3366 20.2814 21.3508 20.2602 21.3604 20.2369C21.3701 20.2135 21.3751 20.1885 21.3751 20.1633C21.3751 20.138 21.3701 20.113 21.3604 20.0897C21.3508 20.0664 21.3366 20.0452 21.3187 20.0273ZM13.3688 13.3688C12.375 14.3602 11.0578 14.9062 9.65625 14.9062C8.25469 14.9062 6.9375 14.3602 5.94375 13.3688C4.95234 12.375 4.40625 11.0578 4.40625 9.65625C4.40625 8.25469 4.95234 6.93516 5.94375 5.94375C6.9375 4.95234 8.25469 4.40625 9.65625 4.40625C11.0578 4.40625 12.3773 4.95 13.3688 5.94375C14.3602 6.9375 14.9062 8.25469 14.9062 9.65625C14.9062 11.0578 14.3602 12.3773 13.3688 13.3688Z"
        fill={fill}
      />
    </Svg>
  );
});
